import React, { useContext, useEffect, useState } from 'react'
import { FlatList, Text, View } from 'react-native'
import { TemplatePage } from '../../../../../components/TemplatePage'
import { globalStyle } from '../../../../../theme/global'
import { FuelContext } from '../../../../../context/Logistics/fuel/FuelProvider'
import { AlertMessage } from '../../../../../components/AlertMessage'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { LogisticsRoutes } from '../../../../../navigator/types'
import { useHasInternet } from '../../../../../hooks/useHasInternet'
import { useTranslation } from 'react-i18next'
import { Order, StateForm } from '../../../../../context/Logistics/fuel/types'
import { textColor } from '../../../../../theme/colors'
import { Filters } from './components/Filters'
import { Header } from './components/Header'
import { Item } from './components/Item'
import { ActionsButtons } from './components/ActionsButtons'
import { useSendFuelRecord } from '../../hooks/useSendFuelRecord'

interface Navigation extends StackNavigationProp<LogisticsRoutes, 'Records'> {}

export const Records = () => {
  const { t } = useTranslation()
  const { getDataByState, deleteRecordById, setRecordByOrderNumber } = useContext(FuelContext)
  const navigation = useNavigation<Navigation>()
  const { isConnected } = useHasInternet()

  const [filterBy, setFilterBy] = useState<StateForm | null>(null)
  const [selected, setSelected] = useState<{ [key: string]: Order }>({})
  const [allSelected, setAllSelected] = useState(false)
  const [showNoInternetAlert, setShowNoInternetAlert] = useState(false)

  const { handleSend, isLoading } = useSendFuelRecord({
    onSuccess(ids) {
      setSelected({})
    },
    onError(ids) {
      setSelected({})
    },
  })

  const visibleRecords = getDataByState(filterBy)

  const handleSelectItem = (item: Order) => {
    setSelected((prev) => {
      const { [item.orderNumber!]: existing, ...rest } = prev
      if (existing) return rest
      return { ...prev, [item.orderNumber!]: item }
    })
  }

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected({})
      setAllSelected(false)
    } else {
      setSelected(Object.fromEntries(visibleRecords.map((r) => [[r.orderNumber], r])))
      setAllSelected(true)
    }
  }

  useEffect(() => {
    const isAll =
      visibleRecords.length > 0 && Object.keys(selected).length === visibleRecords.length
    setAllSelected(isAll)
  }, [selected, filterBy])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getResumeRoute = (record: Order): keyof LogisticsRoutes => {
    const p = record.photos ?? {}
    if (!p.initialPhotoMeter && !record.testResult) return 'InitialPhotoRod'
    if (!p.initialPhotoMeter) return 'InitialPhotoMeter'
    if (!p.finalPhotoMeter) return 'FinalPhotoMeter'
    if (!p.voucherPhoto) return 'VoucherPhoto'
    return 'Summary'
  }

  const handleNavigate = (item: Order) => {
    setRecordByOrderNumber(item.orderNumber!)
    navigation.navigate('Summary', { orderNumber: item.orderNumber! })
  }

  const handleDelete = () => {
    Object.values(selected).forEach((item) => deleteRecordById(item.orderNumber!))
    setSelected({})
  }

  const handleManualSend = () => {
    if (!isConnected) {
      setShowNoInternetAlert(true)
      return
    }
    const items = Object.values(selected).filter((r) => r.stateForm === 'pending')
    if (items.length === 0) return
    handleSend(items)
  }

  const hasSelection = Object.keys(selected).length > 0

  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={t('Registros combustible')}
    >
      <View style={[globalStyle.viewContainer, { padding: 0 }]}>
        <Filters onChange={setFilterBy} />

        {visibleRecords.length > 0 && (
          <FlatList
            data={visibleRecords}
            keyExtractor={(item) => item.orderNumber ?? ''}
            ListHeaderComponent={<Header onPress={handleSelectAll} isCheck={allSelected} />}
            renderItem={({ item, index }) => (
              <Item
                item={item}
                isCheck={!!selected[item.orderNumber!]}
                onSelect={handleSelectItem}
                onNavigate={handleNavigate}
                hasBg={index % 2 === 0}
              />
            )}
          />
        )}

        {hasSelection && !isLoading && (
          <ActionsButtons isLoading={isLoading} onDelete={handleDelete} onSend={handleManualSend} />
        )}

        {showNoInternetAlert && (
          <AlertMessage
            title={t('Sin internet')}
            successLabel='Ok'
            action={() => setShowNoInternetAlert(false)}
          >
            <Text style={{ textAlign: 'center', fontSize: 18, color: textColor }}>
              {t('Necesitas estar conectado a internet para poder enviar los registros')}
            </Text>
          </AlertMessage>
        )}
      </View>
    </TemplatePage>
  )
}
